import {
  Autocomplete,
  Box,
  Chip,
  Dialog,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useMemo, useState } from 'react';
import StyledButton from '../../components/buttons/StyledButton';
import { useAlert, useDatastore } from '../../hooks';
import { captureError } from '../../common/sentry';
import { UserProfileProps } from '../../datastore/userDetails';
import {
  QmSiteInboundLocationAdminOption,
  QmSiteInboundWarehouseAdminOption,
  archiveQmSiteInboundLocationRequest,
  archiveQmSiteInboundWarehouseRequest,
  createQmSiteInboundLocationRequest,
  createQmSiteInboundWarehouseRequest,
  listQmSiteInboundLocationsAdminRequest,
  listQmSiteInboundWarehousesAdminRequest,
  updateQmSiteInboundLocationRequest,
  updateQmSiteInboundWarehouseRequest
} from '../../api/qmCrud';

type FormState = {
  receivingWarehouseId: string;
  code: string;
  label: string;
  displayOrder: string;
};

type WarehouseFormState = {
  receivingWarehouseId: string;
  label: string;
  qmCoordinatorEmails: string[];
  displayOrder: string;
};

interface QmSiteInboundLocationSettingsDialogProps {
  open: boolean;
  canManage: boolean;
  onClose: () => void;
}

const emptyForm: FormState = {
  receivingWarehouseId: '',
  code: '',
  label: '',
  displayOrder: ''
};

const emptyWarehouseForm: WarehouseFormState = {
  receivingWarehouseId: '',
  label: '',
  qmCoordinatorEmails: [],
  displayOrder: ''
};

const SYSTEM_QM_COORDINATOR_REASONS = [
  'Cycle Count In Progress',
  'Warehouse Location Not Active',
  'Other Logged Issue'
];

function sortLocations(a: QmSiteInboundLocationAdminOption, b: QmSiteInboundLocationAdminOption) {
  return (
    a.receivingWarehouseId.localeCompare(b.receivingWarehouseId, undefined, { numeric: true }) ||
    Number(b.active) - Number(a.active) ||
    a.displayOrder - b.displayOrder ||
    a.label.localeCompare(b.label)
  );
}

function sortWarehouses(a: QmSiteInboundWarehouseAdminOption, b: QmSiteInboundWarehouseAdminOption) {
  return Number(b.active) - Number(a.active) || a.displayOrder - b.displayOrder || a.label.localeCompare(b.label);
}

export default function QmSiteInboundLocationSettingsDialog({
  open,
  canManage,
  onClose
}: QmSiteInboundLocationSettingsDialogProps) {
  const alert = useAlert();
  const store = useDatastore();
  const [warehouses, setWarehouses] = useState<QmSiteInboundWarehouseAdminOption[]>([]);
  const [locations, setLocations] = useState<QmSiteInboundLocationAdminOption[]>([]);
  const [userProfiles, setUserProfiles] = useState<UserProfileProps[]>([]);
  const [warehouseForm, setWarehouseForm] = useState<WarehouseFormState>(emptyWarehouseForm);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingWarehouseId, setEditingWarehouseId] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');

  const loadData = useCallback(
    async (preferredWarehouseId?: string) => {
      if (!open || !canManage) return;
      setIsLoading(true);
      try {
        const [warehouseResults, profileResults] = await Promise.all([
          listQmSiteInboundWarehousesAdminRequest(),
          store.userDetails.getAllProfiles()
        ]);
        const sortedWarehouses = [...warehouseResults].sort(sortWarehouses);
        const nextSelectedWarehouseId =
          preferredWarehouseId || sortedWarehouses.find((warehouse) => warehouse.active)?.receivingWarehouseId || '';
        const locationResults = await listQmSiteInboundLocationsAdminRequest(nextSelectedWarehouseId || null);
        setWarehouses(sortedWarehouses);
        setUserProfiles(
          profileResults
            .filter((profile) => profile.email)
            .sort((a, b) =>
              `${a.firstname || ''} ${a.lastname || ''} ${a.email || ''}`.localeCompare(
                `${b.firstname || ''} ${b.lastname || ''} ${b.email || ''}`
              )
            )
        );
        setSelectedWarehouseId(nextSelectedWarehouseId);
        setLocations([...locationResults].sort(sortLocations));
      } catch (error) {
        captureError(error, 'settings-listQmSiteInboundReferenceData');
        alert('Unable to load Site Inbound settings', 'error');
      } finally {
        setIsLoading(false);
      }
    },
    [alert, canManage, open, store.userDetails]
  );

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const activeWarehouses = useMemo(() => warehouses.filter((warehouse) => warehouse.active), [warehouses]);
  const selectedWarehouse = useMemo(
    () => warehouses.find((warehouse) => warehouse.receivingWarehouseId === selectedWarehouseId) ?? null,
    [selectedWarehouseId, warehouses]
  );
  const editingWarehouse = useMemo(
    () => warehouses.find((warehouse) => warehouse.receivingWarehouseId === editingWarehouseId) ?? null,
    [editingWarehouseId, warehouses]
  );
  const selectedCoordinatorProfiles = useMemo(() => {
    const selectedEmails = new Set(warehouseForm.qmCoordinatorEmails.map((email) => email.toLowerCase()));
    return userProfiles.filter((profile) => profile.email && selectedEmails.has(profile.email.toLowerCase()));
  }, [userProfiles, warehouseForm.qmCoordinatorEmails]);

  const editingLocation = useMemo(
    () => locations.find((location) => `${location.receivingWarehouseId}:${location.code}` === editingKey) ?? null,
    [editingKey, locations]
  );

  function resetForm() {
    setForm({ ...emptyForm, receivingWarehouseId: selectedWarehouseId });
    setEditingKey(null);
  }

  function resetWarehouseForm() {
    setWarehouseForm(emptyWarehouseForm);
    setEditingWarehouseId(null);
  }

  function startEditWarehouse(warehouse: QmSiteInboundWarehouseAdminOption) {
    setEditingWarehouseId(warehouse.receivingWarehouseId);
    setWarehouseForm({
      receivingWarehouseId: warehouse.receivingWarehouseId,
      label: warehouse.label,
      qmCoordinatorEmails: warehouse.qmCoordinatorEmails,
      displayOrder: String(warehouse.displayOrder)
    });
  }

  function startEdit(location: QmSiteInboundLocationAdminOption) {
    setEditingKey(`${location.receivingWarehouseId}:${location.code}`);
    setForm({
      receivingWarehouseId: location.receivingWarehouseId,
      code: location.code,
      label: location.label,
      displayOrder: String(location.displayOrder)
    });
  }

  async function saveWarehouse() {
    if (!canManage) return;
    const receivingWarehouseId = warehouseForm.receivingWarehouseId.trim();
    const label = warehouseForm.label.trim();
    const qmCoordinatorEmails = warehouseForm.qmCoordinatorEmails;
    if (!receivingWarehouseId || !/^\d+$/.test(receivingWarehouseId)) {
      alert('Enter a valid receiving warehouse ID', 'warning');
      return;
    }
    if (!label) {
      alert('Enter a receiving warehouse label', 'warning');
      return;
    }

    setIsSaving(true);
    const input = {
      receivingWarehouseId,
      label,
      qmCoordinatorEmails,
      displayOrder: warehouseForm.displayOrder.trim() === '' ? null : Number(warehouseForm.displayOrder)
    };
    try {
      if (editingWarehouse) {
        await updateQmSiteInboundWarehouseRequest(input);
        alert('Site Inbound warehouse updated', 'success');
      } else {
        await createQmSiteInboundWarehouseRequest(input);
        alert('Site Inbound warehouse saved', 'success');
      }
      setSelectedWarehouseId(receivingWarehouseId);
      resetWarehouseForm();
      await loadData(receivingWarehouseId);
    } catch (error) {
      captureError(error, 'settings-saveQmSiteInboundWarehouse');
      alert('Unable to save Site Inbound warehouse', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function archiveWarehouse(warehouse: QmSiteInboundWarehouseAdminOption) {
    if (!canManage) return;
    setIsSaving(true);
    try {
      if (warehouse.active) {
        await archiveQmSiteInboundWarehouseRequest({ receivingWarehouseId: warehouse.receivingWarehouseId });
        alert('Site Inbound warehouse archived', 'success');
      } else {
        await createQmSiteInboundWarehouseRequest({
          receivingWarehouseId: warehouse.receivingWarehouseId,
          label: warehouse.label,
          qmCoordinatorEmails: warehouse.qmCoordinatorEmails,
          displayOrder: warehouse.displayOrder
        });
        alert('Site Inbound warehouse reactivated', 'success');
      }
      if (selectedWarehouseId === warehouse.receivingWarehouseId && warehouse.active) {
        setSelectedWarehouseId('');
      }
      await loadData(
        selectedWarehouseId === warehouse.receivingWarehouseId && warehouse.active ? '' : selectedWarehouseId
      );
    } catch (error) {
      captureError(error, 'settings-archiveQmSiteInboundWarehouse');
      alert('Unable to update Site Inbound warehouse status', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function saveLocation() {
    if (!canManage) return;
    const receivingWarehouseId = form.receivingWarehouseId.trim() || selectedWarehouseId;
    const code = form.code.trim();
    const label = form.label.trim();
    if (!receivingWarehouseId || !/^\d+$/.test(receivingWarehouseId)) {
      alert('Enter a valid receiving warehouse ID', 'warning');
      return;
    }
    if (!code || !label) {
      alert('Enter location code and label', 'warning');
      return;
    }

    setIsSaving(true);
    const input = {
      receivingWarehouseId,
      code,
      label,
      displayOrder: form.displayOrder.trim() === '' ? null : Number(form.displayOrder)
    };
    try {
      if (editingLocation) {
        await updateQmSiteInboundLocationRequest(input);
        alert('Site Inbound location updated', 'success');
      } else {
        await createQmSiteInboundLocationRequest(input);
        alert('Site Inbound location saved', 'success');
      }
      resetForm();
      await loadData(receivingWarehouseId);
    } catch (error) {
      captureError(error, 'settings-saveQmSiteInboundLocation');
      alert('Unable to save Site Inbound location', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  async function archiveLocation(location: QmSiteInboundLocationAdminOption) {
    if (!canManage) return;
    setIsSaving(true);
    try {
      if (location.active) {
        await archiveQmSiteInboundLocationRequest({
          receivingWarehouseId: location.receivingWarehouseId,
          code: location.code
        });
        alert('Site Inbound location archived', 'success');
      } else {
        await createQmSiteInboundLocationRequest({
          receivingWarehouseId: location.receivingWarehouseId,
          code: location.code,
          label: location.label,
          displayOrder: location.displayOrder
        });
        alert('Site Inbound location reactivated', 'success');
      }
      await loadData(location.receivingWarehouseId);
    } catch (error) {
      captureError(error, 'settings-archiveQmSiteInboundLocation');
      alert('Unable to update Site Inbound location status', 'error');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Stack spacing={2} sx={{ p: 2.5 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack spacing={0.4}>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' }}>
              Site Inbound Location List
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage receiving warehouse labels and item locations for Site Inbound QMs.
            </Typography>
          </Stack>
          <IconButton onClick={onClose} size="small" aria-label="Close Site Inbound Location settings">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        {!canManage && (
          <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'rgba(248,113,113,0.45)', bgcolor: '#fff7ed' }}>
            <Typography variant="body2" color="error">
              You do not have permission to manage QM location reference data.
            </Typography>
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'rgba(148,163,184,0.32)' }}>
          <Stack spacing={1.25}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 800 }}>
              {editingWarehouse ? 'Edit Warehouse' : 'Add Warehouse'}
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
              <TextField
                label="Receiving Warehouse ID"
                size="small"
                value={warehouseForm.receivingWarehouseId}
                onChange={(event) =>
                  setWarehouseForm((current) => ({ ...current, receivingWarehouseId: event.target.value }))
                }
                disabled={!canManage || isSaving || Boolean(editingWarehouse)}
              />
              <TextField
                label="Warehouse Label"
                size="small"
                value={warehouseForm.label}
                onChange={(event) => setWarehouseForm((current) => ({ ...current, label: event.target.value }))}
                disabled={!canManage || isSaving}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Display Order"
                size="small"
                type="number"
                value={warehouseForm.displayOrder}
                onChange={(event) => setWarehouseForm((current) => ({ ...current, displayOrder: event.target.value }))}
                disabled={!canManage || isSaving}
                sx={{ width: { xs: '100%', md: 150 } }}
              />
            </Stack>
            <Autocomplete
              multiple
              size="small"
              options={userProfiles}
              value={selectedCoordinatorProfiles}
              onChange={(_, selectedProfiles) =>
                setWarehouseForm((current) => ({
                  ...current,
                  qmCoordinatorEmails: Array.from(
                    new Set(
                      selectedProfiles
                        .map((profile) => profile.email?.trim().toLowerCase())
                        .filter((email): email is string => Boolean(email))
                    )
                  )
                }))
              }
              getOptionLabel={(profile) =>
                `${profile.firstname || ''} ${profile.lastname || ''}`.trim() || profile.email || profile.cognitoID
              }
              isOptionEqualToValue={(option, value) => option.cognitoID === value.cognitoID}
              renderTags={(value, getTagProps) =>
                value.map((profile, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={profile.cognitoID}
                    label={`${profile.firstname || ''} ${profile.lastname || ''}`.trim() || profile.email}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="QM Coordinators"
                  helperText={`Only used for ${SYSTEM_QM_COORDINATOR_REASONS.join(', ')}.`}
                />
              )}
              disabled={!canManage || isSaving}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {editingWarehouse && (
                <StyledButton variant="outlined" onClick={resetWarehouseForm} disabled={isSaving}>
                  Cancel Edit
                </StyledButton>
              )}
              <StyledButton variant="contained" onClick={() => void saveWarehouse()} disabled={!canManage || isSaving}>
                {editingWarehouse ? 'Update Warehouse' : 'Save Warehouse'}
              </StyledButton>
            </Stack>
            <Stack spacing={0.75}>
              {warehouses.map((warehouse) => (
                <Paper
                  key={warehouse.receivingWarehouseId}
                  variant="outlined"
                  sx={{ p: 1, borderColor: 'rgba(148,163,184,0.24)' }}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 800 }}>{warehouse.label}</Typography>
                      <Chip size="small" label={`ID ${warehouse.receivingWarehouseId}`} variant="outlined" />
                      <Chip
                        size="small"
                        label={warehouse.active ? 'Active' : 'Archived'}
                        color={warehouse.active ? 'success' : 'default'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Display order {warehouse.displayOrder}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${warehouse.qmCoordinatorEmails.length} QM coordinators`}
                        variant="outlined"
                      />
                    </Stack>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <StyledButton
                        variant="outlined"
                        onClick={() => startEditWarehouse(warehouse)}
                        disabled={!canManage || isSaving || !warehouse.active}>
                        Edit
                      </StyledButton>
                      <StyledButton
                        variant="outlined"
                        onClick={() => void archiveWarehouse(warehouse)}
                        disabled={!canManage || isSaving}>
                        {warehouse.active ? 'Archive' : 'Reactivate'}
                      </StyledButton>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Autocomplete
            size="small"
            options={activeWarehouses}
            value={selectedWarehouse}
            onChange={(_, warehouse) => {
              const nextWarehouseId = warehouse?.receivingWarehouseId || '';
              setSelectedWarehouseId(nextWarehouseId);
              setEditingKey(null);
              setLocations([]);
              setForm({ ...emptyForm, receivingWarehouseId: nextWarehouseId });
              void loadData(nextWarehouseId);
            }}
            getOptionLabel={(warehouse) => `${warehouse.label} (${warehouse.receivingWarehouseId})`}
            isOptionEqualToValue={(option, value) => option.receivingWarehouseId === value.receivingWarehouseId}
            renderInput={(params) => <TextField {...params} label="Configured Warehouse" />}
            disabled={!canManage || isLoading || isSaving}
            sx={{ minWidth: { xs: '100%', sm: 320 } }}
          />
          <StyledButton
            variant="outlined"
            onClick={() => void loadData(selectedWarehouseId)}
            disabled={!canManage || isLoading || isSaving}>
            Refresh
          </StyledButton>
        </Stack>

        <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'rgba(148,163,184,0.32)' }}>
          <Stack spacing={1.25}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 800 }}>
              {editingLocation ? 'Edit Location' : 'Add Location'}
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
              <TextField
                label="Receiving Warehouse"
                size="small"
                value={
                  selectedWarehouse ? `${selectedWarehouse.label} (${selectedWarehouse.receivingWarehouseId})` : ''
                }
                disabled
              />
              <TextField
                label="Code"
                size="small"
                value={form.code}
                onChange={(event) => setForm((current) => ({ ...current, code: event.target.value }))}
                disabled={!canManage || isSaving || Boolean(editingLocation)}
              />
              <TextField
                label="Label"
                size="small"
                value={form.label}
                onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))}
                disabled={!canManage || isSaving}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Display Order"
                size="small"
                type="number"
                value={form.displayOrder}
                onChange={(event) => setForm((current) => ({ ...current, displayOrder: event.target.value }))}
                disabled={!canManage || isSaving}
                sx={{ width: { xs: '100%', md: 150 } }}
              />
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {editingLocation && (
                <StyledButton variant="outlined" onClick={resetForm} disabled={isSaving}>
                  Cancel Edit
                </StyledButton>
              )}
              <StyledButton variant="contained" onClick={() => void saveLocation()} disabled={!canManage || isSaving}>
                {editingLocation ? 'Update Location' : 'Save Location'}
              </StyledButton>
            </Stack>
          </Stack>
        </Paper>

        <Divider />

        <Stack spacing={1} sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
          {locations.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {isLoading ? 'Loading locations...' : 'No Site Inbound locations found.'}
            </Typography>
          )}
          {locations.map((location) => (
            <Paper
              key={`${location.receivingWarehouseId}:${location.code}`}
              variant="outlined"
              sx={{ p: 1.25, borderColor: location.active ? 'rgba(148,163,184,0.32)' : 'rgba(148,163,184,0.18)' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.25}
                alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Stack spacing={0.3} sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 800 }}>{location.label}</Typography>
                    <Chip size="small" label={location.code} />
                    <Chip
                      size="small"
                      label={`${location.receivingWarehouseLabel || 'Warehouse'} ${location.receivingWarehouseId}`}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={location.active ? 'Active' : 'Archived'}
                      color={location.active ? 'success' : 'default'}
                      variant={location.active ? 'filled' : 'outlined'}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Display order {location.displayOrder}
                  </Typography>
                </Stack>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                  <StyledButton
                    variant="outlined"
                    onClick={() => startEdit(location)}
                    disabled={!canManage || isSaving || !location.active}>
                    Edit
                  </StyledButton>
                  <StyledButton
                    variant="outlined"
                    onClick={() => void archiveLocation(location)}
                    disabled={!canManage || isSaving}>
                    {location.active ? 'Archive' : 'Reactivate'}
                  </StyledButton>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Stack>
    </Dialog>
  );
}
